import { ArrowLeft, ArrowRight } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "../../elements/button/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../elements/select/select";
import { Text } from "../../elements/text/text";

export type CartHeaderProps = {
  microcopies: {
    cartTitle: string;
    articlesDescription: string;
    totalAmountLabel: string;
    orderPurposeLabel?: string;
    backButton: string;
    checkoutButton: string;
    placeOrderButton: string;
    validateButton: string;
    clearCartButton?: string;
  };
  data: {
    totalAmount: string;
    selectedOrderPurpose?: string;
  };
  orderPurpose?: {
    mode: "dropdown" | "readonly" | "hidden" | "none";
    options?: Array<{
      value: string;
      label: string;
    }>;
    onValueChange?: (value: string) => void;
  };
  buttons: {
    showBack?: boolean;
    showThreeDots?: boolean;
    showCheckout?: boolean;
    showPlaceOrder?: boolean;
    showValidate?: boolean;
    onBackClick?: () => void;
    onThreeDotsClick?: () => void;
    onCheckoutClick?: () => void;
    onPlaceOrderClick?: () => void;
    onValidateClick?: () => void;
    onClearCartClick?: () => void;
  };
  cartMenuOptions: ReactNode;
  placeOrderEnabled?: boolean;
};

type OrderPurposeProps = {
  mode: "readonly" | "dropdown" | "hidden" | "none";
  selectedValue?: string;
  options?: Array<{ value: string; label: string }>;
  onValueChange?: (value: string) => void;
  selectWidth?: string;
};

/**
 * Helper component that renders order purpose information.
 * Displays as plain text for read-only mode or as a dropdown for editable mode.
 * @internal - Used internally by CartHeader component
 */
function OrderPurposeSection(props: Readonly<OrderPurposeProps>) {
  const readOnlyOption =
    props.options?.find((option) => option.value === props.selectedValue)
      ?.label ?? props.selectedValue;

  if (props.mode === "readonly") {
    return <Text variant="body-md">{readOnlyOption}</Text>;
  }

  return (
    <Select onValueChange={props.onValueChange} value={props.selectedValue}>
      <SelectTrigger className={`h-8 ${props.selectWidth || "w-full"}`}>
        <SelectValue placeholder={props.selectedValue} />
      </SelectTrigger>
      <SelectContent>
        {props.options?.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

type ActionButtonsProps = {
  buttons: CartHeaderProps["buttons"];
  placeOrderEnabled?: boolean;
  cartMenuOptions: CartHeaderProps["cartMenuOptions"];
  microcopies: Pick<
    CartHeaderProps["microcopies"],
    "backButton" | "checkoutButton" | "placeOrderButton" | "validateButton"
  >;
};

/**
 * Helper component that renders action buttons based on configuration.
 * Handles back, checkout, place order, validate, and three-dots menu buttons.
 * @internal - Used internally by CartHeader component
 */
function ActionButtons(props: Readonly<ActionButtonsProps>) {
  return (
    <>
      {props.buttons.showBack && (
        <Button
          aria-label={props.microcopies.backButton}
          onClick={props.buttons.onBackClick}
          size="icon"
          variant="outline"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
      )}

      {props.buttons.showCheckout && (
        <Button onClick={props.buttons.onCheckoutClick}>
          <ArrowRight className="h-4 w-4" />
          {props.microcopies.checkoutButton}
        </Button>
      )}

      {props.buttons.showPlaceOrder && (
        <Button
          disabled={!props.placeOrderEnabled}
          onClick={props.buttons.onPlaceOrderClick}
        >
          <ArrowRight className="h-4 w-4" />
          {props.microcopies.placeOrderButton}
        </Button>
      )}

      {props.buttons.showValidate && (
        <Button onClick={props.buttons.onValidateClick}>
          {props.microcopies.validateButton}
        </Button>
      )}

      {props.buttons.showThreeDots && props.cartMenuOptions}
    </>
  );
}

export function CartHeader(props: Readonly<CartHeaderProps>) {
  const showOrderPurpose =
    props.orderPurpose?.mode !== "hidden" &&
    props.orderPurpose?.mode !== "none";
  // Auto-determine 3-dots visibility based on order purpose mode:
  // - Editable: show 3-dots (for clear cart option)
  // - Read-only: hide 3-dots (checkout/order confirmation pages)
  // - None/hidden: show 3-dots (for clear cart option)
  const showThreeDots =
    props.buttons.showThreeDots ?? props.orderPurpose?.mode !== "readonly";

  return (
    <>
      {/* Mobile Layout (default) */}
      <div className="flex flex-col gap-3 md:hidden">
        {/* Title and Description */}
        <div className="flex flex-col gap-1">
          <Text variant="h3">{props.microcopies.cartTitle}</Text>
          <Text variant="body-md">{props.microcopies.articlesDescription}</Text>
        </div>

        {/* Amount */}
        <div className="flex items-center justify-between gap-1">
          <Text variant="body-md">{props.microcopies.totalAmountLabel}</Text>
          <div className="text-right">
            <Text variant="h3">{props.data.totalAmount}</Text>
          </div>
        </div>

        {/* Order Purpose (Mobile) */}
        {showOrderPurpose && props.orderPurpose && (
          <div className="flex flex-col gap-2">
            <Text variant="body-sm">{props.microcopies.orderPurposeLabel}</Text>
            <OrderPurposeSection
              mode={props.orderPurpose.mode as "readonly" | "dropdown"}
              onValueChange={props.orderPurpose.onValueChange}
              options={props.orderPurpose.options}
              selectedValue={props.data.selectedOrderPurpose}
            />
          </div>
        )}

        {/* Action Buttons (Mobile) */}
        <div className="flex items-center justify-end gap-4">
          <ActionButtons
            buttons={{ ...props.buttons, showThreeDots }}
            cartMenuOptions={props.cartMenuOptions}
            microcopies={props.microcopies}
            placeOrderEnabled={props.placeOrderEnabled}
          />
        </div>
      </div>

      {/* Desktop Layout (md and up) */}
      <div className="hidden md:flex md:items-center md:justify-between md:gap-5">
        {/* Left Section */}
        <div className="flex flex-col justify-center gap-1">
          <Text variant="h3">{props.microcopies.cartTitle}</Text>
          <Text variant="body-md">{props.microcopies.articlesDescription}</Text>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-5">
          {/* Amount Section */}
          <div className="flex flex-col items-end justify-center gap-1">
            <div className="text-right">
              <Text variant="h3">{props.data.totalAmount}</Text>
            </div>
            <div className="text-right">
              <Text variant="body-md">
                {props.microcopies.totalAmountLabel}
              </Text>
            </div>
          </div>

          {/* Order Purpose Section (Desktop) */}
          {showOrderPurpose && props.orderPurpose && (
            <div className="flex flex-col items-start justify-between gap-0.5">
              <Text variant="body-sm">
                {props.microcopies.orderPurposeLabel}
              </Text>
              <OrderPurposeSection
                mode={props.orderPurpose?.mode as "readonly" | "dropdown"}
                onValueChange={props.orderPurpose?.onValueChange}
                options={props.orderPurpose?.options}
                selectedValue={props.data.selectedOrderPurpose}
                selectWidth="w-[200px]"
              />
            </div>
          )}

          {/* Action Buttons (Desktop) */}
          <div className="flex items-center gap-4">
            <ActionButtons
              buttons={{ ...props.buttons, showThreeDots }}
              cartMenuOptions={props.cartMenuOptions}
              microcopies={props.microcopies}
              placeOrderEnabled={props.placeOrderEnabled}
            />
          </div>
        </div>
      </div>
    </>
  );
}
